import { AzureUser, AzureLoginResponse, AzureCallbackResponse, ApiError } from '../types';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

class AzureAuthService {
  private baseURL: string;

  constructor() {
    this.baseURL = BACKEND_URL;
  }

  /**
   * Initiate Azure AD SSO login
   * Redirects user to Azure login page
   */
  async initiateAzureLogin(): Promise<void> {
    try {
      // Add callback URL to the request
      const callbackUrl = `${window.location.origin}/auth/azure/oauth2callback`;
      const loginUrl = `${this.baseURL}/api/auth/azure/login?callback_url=${encodeURIComponent(callbackUrl)}`;
      
      // Direct redirect to backend endpoint
      window.location.href = loginUrl;
    } catch (error) {
      console.error('Azure login initiation error:', error);
      throw new Error('Failed to initiate Azure login. Please try again.');
    }
  }

  /**
   * Handle Azure callback with authorization code and state
   * This should be called from the callback page
   */
  async handleAzureCallback(code: string, state?: string): Promise<AzureCallbackResponse> {
    try {
      let url = `${this.baseURL}/api/auth/azure/callback?code=${code}`;
      if (state) {
        url += `&state=${state}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data: AzureCallbackResponse = await response.json();
        return data;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Azure callback failed');
      }
    } catch (error) {
      console.error('Azure callback error:', error);
      throw new Error('Azure authentication failed. Please try again.');
    }
  }

  /**
   * Get current user information
   */
  async getCurrentUser(): Promise<AzureUser> {
    try {
      const token = this.getStoredToken();
      if (!token) {
        throw new Error('No authentication token found');
      }

      const response = await fetch(`${this.baseURL}/api/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.ok) {
        const user: AzureUser = await response.json();
        return user;
      } else if (response.status === 401) {
        // Token expired or invalid
        this.clearStoredToken();
        throw new Error('Authentication token expired');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to get user information');
      }
    } catch (error) {
      console.error('Get current user error:', error);
      throw error;
    }
  }

  /**
   * Verify if current token is valid
   */
  async verifyToken(): Promise<boolean> {
    try {
      const token = this.getStoredToken();
      if (!token) {
        return false;
      }

      const response = await fetch(`${this.baseURL}/api/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      return response.ok;
    } catch (error) {
      console.error('Token verification error:', error);
      return false;
    }
  }

  /**
   * Store JWT token in localStorage
   */
  storeToken(token: string): void {
    localStorage.setItem('azureAuthToken', token);
  }

  /**
   * Get stored JWT token
   */
  getStoredToken(): string | null {
    return localStorage.getItem('azureAuthToken');
  }

  /**
   * Clear stored JWT token
   */
  clearStoredToken(): void {
    localStorage.removeItem('azureAuthToken');
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      const token = this.getStoredToken();
      if (token) {
        await fetch(`${this.baseURL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      this.clearStoredToken();
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.getStoredToken() !== null;
  }
}

export const azureAuthService = new AzureAuthService();
export default azureAuthService;
